import { createClient, parseNaturalLanguage } from "@app/ai";
import { Button, Card, Input, Sparkles } from "@app/ui";
import { useId, useState } from "react";

import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore, useTasksStore } from "../../stores/index.js";
import { InvalidQuickAddDraftError, quickAddInputFromDraft } from "./quick-add-input.js";

export function AiQuickAdd() {
  const { t } = useI18n();
  const anthropicApiKey = useSettingsStore((s) => s.anthropicApiKey);
  const defaultDurationMin = useSettingsStore((s) => s.defaultDurationMin);
  const defaultReminderMinutes = useSettingsStore((s) => s.defaultReminderMinutes);
  const add = useTasksStore((s) => s.add);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();
  const errorId = `${inputId}-error`;

  async function go() {
    setError(null);
    if (!anthropicApiKey) {
      setError(t("aiQuickAdd.missingKey"));
      return;
    }
    const prompt = text.trim();
    if (!prompt || busy) return;
    setBusy(true);
    try {
      const draft = await parseNaturalLanguage(createClient(anthropicApiKey), prompt);
      await add(quickAddInputFromDraft(draft, {
        durationMinutes: defaultDurationMin,
        reminderOffsets: defaultReminderMinutes,
      }));
      setText("");
    } catch (e) {
      setError(e instanceof InvalidQuickAddDraftError
        ? t("aiQuickAdd.invalidResponse")
        : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-3">
      <label htmlFor={inputId} className="mb-2 flex items-center gap-2 text-sm font-medium">
        <Sparkles className="h-4 w-4" /> {t("aiQuickAdd.title")}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id={inputId}
          className="min-w-0 flex-1"
          placeholder={t("aiQuickAdd.placeholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) void go();
          }}
          disabled={busy}
          aria-describedby={error ? errorId : undefined}
        />
        <Button className="whitespace-nowrap" onClick={() => void go()} disabled={busy}>
          {busy ? t("aiQuickAdd.thinking") : t("common.add")}
        </Button>
      </div>
      {error && <p id={errorId} role="alert" className="mt-2 text-sm text-destructive">{error}</p>}
    </Card>
  );
}
